import { useEffect, useRef, useState, type FormEvent } from 'react';
import { recipeThumbUrl, type Recipe } from '@awwyeah/shared';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2, Plus, Sparkles, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../auth';
import { RecipeImportError, importRecipeFromUrl } from '../api';
import { draftFromCleanRecipe, pollAiJob, requestAiImport } from '../aiImport';
import {
  RECIPES_MAP_KEY,
  createRecipe,
  updateRecipe,
  useRecipe,
  useRecipesDoc,
  type RecipeDraft,
} from '../hooks';
import { parseIngredientLine } from '../utils/parseIngredientLine';

type ImportState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'done'; ingredientCount: number; stepCount: number; warnings: string[] }
  | { phase: 'failed'; error: string };

/**
 * The WS-18 cleanup pass, offered only after tier 1 came back short. Its states are
 * deliberately gentle: `napping` and `waiting` are not errors and don't look like errors —
 * an LLM outage must degrade silently (docs/AI-PIPELINE.md), and the form below is already
 * usable either way.
 */
type CleanupState =
  | { phase: 'idle' }
  | { phase: 'working' }
  | { phase: 'done'; ingredientCount: number; stepCount: number }
  /** Queued, but the assistant didn't answer while we watched. It still will. */
  | { phase: 'waiting' }
  | { phase: 'napping' };

/**
 * The cleanup offer appears exactly when tier 1 didn't fully succeed: either it failed, or
 * it succeeded with warnings (no title / no ingredients / no steps). A clean tier-1 import
 * needs no model and is never offered one.
 */
function showCleanupOffer(state: ImportState): boolean {
  if (state.phase === 'failed') return true;
  return state.phase === 'done' && state.warnings.length > 0;
}

function emptyDraft(): RecipeDraft {
  return {
    title: '',
    ingredients: [{ text: '' }],
    steps: [''],
    tags: [],
    notes: undefined,
    sourceUrl: undefined,
  };
}

export function RecipeFormPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { user } = useUser();
  const doc = useRecipesDoc();
  const existing = useRecipe(doc, id);

  const [mode, setMode] = useState<'manual' | 'import'>(isEditing ? 'manual' : 'import');
  const [draft, setDraft] = useState<RecipeDraft>(() =>
    existing
      ? {
          title: existing.title,
          ingredients: existing.ingredients.map((i) => ({ text: i.text })),
          steps: existing.steps,
          tags: existing.tags,
          notes: existing.notes,
          sourceUrl: existing.sourceUrl,
          servings: existing.servings,
          photoRef: existing.photoRef,
        }
      : emptyDraft(),
  );
  const [url, setUrl] = useState('');
  const [importState, setImportState] = useState<ImportState>({ phase: 'idle' });
  const [cleanup, setCleanup] = useState<CleanupState>({ phase: 'idle' });
  const [tagInput, setTagInput] = useState('');

  // Navigating away mid-poll must not keep fetching or setState on an unmounted form.
  const cleanupAbort = useRef<AbortController | null>(null);
  useEffect(() => () => cleanupAbort.current?.abort(), []);

  /**
   * "✨ Clean it up with the assistant" — offered only when tier 1 failed or came back
   * partial. It calls `/api/ai/import-recipe`, which re-fetches server-side, retries the
   * structured parse, and (still short) enqueues a `clean-recipe` job we then poll.
   *
   * It drops the result into the form and stops. It never saves — the user reviews and
   * presses save, exactly as with a manual entry.
   */
  async function handleCleanup(): Promise<void> {
    if (!url.trim()) return;
    cleanupAbort.current?.abort();
    const controller = new AbortController();
    cleanupAbort.current = controller;
    setCleanup({ phase: 'working' });

    try {
      const response = await requestAiImport(url.trim());

      if (response.status === 'failed') {
        setCleanup({ phase: 'napping' });
        return;
      }

      if (response.status === 'complete') {
        // The second look found what the first didn't — no model call needed at all.
        applyImported(response.recipe);
        setCleanup({
          phase: 'done',
          ingredientCount: response.recipe.ingredients.length,
          stepCount: response.recipe.steps.length,
        });
        return;
      }

      const outcome = await pollAiJob(response.jobId, { signal: controller.signal });
      if (controller.signal.aborted) return;

      if (outcome.status === 'done') {
        let counts = { ingredientCount: 0, stepCount: 0 };
        setDraft((prev) => {
          const next = draftFromCleanRecipe(outcome.result, prev);
          counts = { ingredientCount: next.ingredients.length, stepCount: next.steps.length };
          return next;
        });
        setCleanup({ phase: 'done', ...counts });
        return;
      }

      // `waiting` (still queued — probably an outage), `invalid` and `failed` all land in
      // the same quiet place. The recipe is not lost; the form below still works.
      setCleanup({ phase: outcome.status === 'waiting' ? 'waiting' : 'napping' });
    } catch {
      setCleanup({ phase: 'napping' });
    }
  }

  function applyImported(recipe: {
    title?: string;
    ingredients: string[];
    steps: string[];
    servings?: number;
    notes?: string;
    sourceUrl?: string;
    photoRef?: RecipeDraft['photoRef'];
  }): void {
    setDraft((prev) => ({
      ...prev,
      title: recipe.title ?? prev.title,
      ingredients:
        recipe.ingredients.length > 0
          ? recipe.ingredients.map((text) => ({ text }))
          : prev.ingredients,
      steps: recipe.steps.length > 0 ? recipe.steps : prev.steps,
      servings: recipe.servings ?? prev.servings,
      notes: recipe.notes ?? prev.notes,
      sourceUrl: recipe.sourceUrl ?? prev.sourceUrl,
      // WS-20: the second look (`clean-recipe`'s tier-1 re-parse) can find a photo the
      // first fetch's `POST /api/recipes/import` didn't need to — never drop one already
      // in the draft, but a freshly-found one wins.
      photoRef: recipe.photoRef ?? prev.photoRef,
    }));
  }

  async function handleImport(): Promise<void> {
    if (!url.trim()) return;
    setImportState({ phase: 'loading' });
    cleanupAbort.current?.abort();
    setCleanup({ phase: 'idle' });
    try {
      const result = await importRecipeFromUrl(url.trim());
      if (result.ok) {
        setDraft({
          title: result.recipe.title ?? '',
          ingredients: result.recipe.ingredients.map((text) => ({ text })) ?? [{ text: '' }],
          steps: result.recipe.steps.length > 0 ? result.recipe.steps : [''],
          tags: result.recipe.tags ?? [],
          notes: result.recipe.notes,
          sourceUrl: result.recipe.sourceUrl,
          servings: result.recipe.servings,
          photoRef: result.recipe.photoRef,
        });
        setImportState({
          phase: 'done',
          ingredientCount: result.recipe.ingredients.length,
          stepCount: result.recipe.steps.length,
          warnings: result.warnings,
        });
      } else {
        // DESIGN-BRIEF §5: never block manual entry — seed whatever was salvaged.
        if (result.partial) {
          setDraft((prev) => ({
            ...prev,
            ingredients: result.partial?.ingredients?.map((text) => ({ text })) ?? prev.ingredients,
            steps: result.partial?.steps?.length ? result.partial.steps : prev.steps,
            sourceUrl: result.partial?.sourceUrl ?? prev.sourceUrl,
            title: result.partial?.title ?? prev.title,
          }));
        }
        setImportState({ phase: 'failed', error: result.error });
      }
    } catch (error) {
      setImportState({
        phase: 'failed',
        error: error instanceof RecipeImportError ? error.message : 'unexpected_error',
      });
    }
  }

  function handleSave(event: FormEvent): void {
    event.preventDefault();
    if (!user) return;
    const map = doc.getMap<Recipe>(RECIPES_MAP_KEY);
    if (isEditing && id) {
      updateRecipe(map, id, draft, user.id);
      void navigate(`/recipes/${id}`);
    } else {
      const newId = createRecipe(map, draft, user.id);
      void navigate(`/recipes/${newId}`);
    }
  }

  function updateIngredient(index: number, text: string): void {
    setDraft((prev) => {
      const ingredients = [...prev.ingredients];
      const parsed = parseIngredientLine(text);
      ingredients[index] = {
        // Keep the user's raw text as the controlled value — the parser trims, and
        // writing parsed.text back made trailing spaces vanish as you typed ("space
        // bar doesn't work"). parseIngredientLine re-normalizes at save time.
        text,
        quantity: parsed.quantity,
        unit: parsed.unit,
        name: parsed.name,
      };
      return { ...prev, ingredients };
    });
  }

  function addIngredientRow(): void {
    setDraft((prev) => ({ ...prev, ingredients: [...prev.ingredients, { text: '' }] }));
  }

  function removeIngredientRow(index: number): void {
    setDraft((prev) => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== index) }));
  }

  function updateStep(index: number, text: string): void {
    setDraft((prev) => {
      const steps = [...prev.steps];
      steps[index] = text;
      return { ...prev, steps };
    });
  }

  function addStep(): void {
    setDraft((prev) => ({ ...prev, steps: [...prev.steps, ''] }));
  }

  function removeStep(index: number): void {
    setDraft((prev) => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index) }));
  }

  function addTag(): void {
    const tag = tagInput.trim();
    if (!tag || draft.tags.includes(tag)) return;
    setDraft((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    setTagInput('');
  }

  function removeTag(tag: string): void {
    setDraft((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  }

  return (
    <form onSubmit={handleSave} className="flex min-h-full flex-col gap-4 pb-28">
      <header className="flex items-center gap-2 px-2 py-3">
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Back"
          className="flex h-11 w-11 items-center justify-center text-fg-2"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="font-display text-lg tracking-tight text-fg">
          {isEditing ? 'Edit recipe' : 'Add a recipe'}
        </h1>
      </header>

      {!isEditing && (
        <div className="flex gap-2 px-4" data-testid="recipe-form-mode-toggle">
          <button
            type="button"
            onClick={() => setMode('import')}
            className={`h-10 flex-1 rounded-md text-[14px] font-semibold ${
              mode === 'import' ? 'bg-inverse text-inverse-fg' : 'border border-line text-fg-2'
            }`}
          >
            From a URL
          </button>
          <button
            type="button"
            onClick={() => setMode('manual')}
            className={`h-10 flex-1 rounded-md text-[14px] font-semibold ${
              mode === 'manual' ? 'bg-inverse text-inverse-fg' : 'border border-line text-fg-2'
            }`}
          >
            Type it in
          </button>
        </div>
      )}

      {mode === 'import' && !isEditing && (
        <div className="flex flex-col gap-2 px-4" data-testid="recipe-import-panel">
          <div className="flex h-11 items-center gap-2 rounded border-2 border-line-strong bg-surface px-3">
            <input
              data-testid="recipe-import-url-input"
              type="url"
              inputMode="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/a-good-recipe"
              className="w-full bg-transparent text-[14px] text-fg-2 focus:outline-none"
            />
          </div>
          <button
            type="button"
            data-testid="recipe-import-fetch-button"
            onClick={() => void handleImport()}
            disabled={!url.trim() || importState.phase === 'loading'}
            className="h-10 self-start rounded-md bg-inverse px-4 text-[14px] font-semibold text-inverse-fg disabled:opacity-50"
          >
            {importState.phase === 'loading' ? 'Reading…' : 'Fetch recipe'}
          </button>

          {importState.phase === 'loading' && (
            <div
              className="flex items-center gap-2.5 rounded-md border border-line bg-surface p-3"
              data-testid="recipe-import-loading"
            >
              <Loader2
                className="h-4 w-4 shrink-0 animate-spin text-sync-busy"
                aria-hidden="true"
              />
              <div>
                <div className="text-[14px] font-semibold text-fg">Reading the page…</div>
                <div className="text-xs text-fg-3">A second or two</div>
              </div>
            </div>
          )}

          {importState.phase === 'done' && (
            <div
              className="flex items-center gap-2.5 rounded-md border border-line bg-surface p-3"
              data-testid="recipe-import-success"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0 text-sync-ok" aria-hidden="true" />
              <div>
                <div className="text-[14px] font-semibold text-fg">
                  Got it — {importState.ingredientCount} ingredients, {importState.stepCount} steps
                </div>
                <div className="text-xs text-fg-3">Give it a look below. Parsers lie.</div>
              </div>
            </div>
          )}

          {importState.phase === 'failed' && (
            <div
              className="flex flex-col gap-2.5 rounded-md border border-line bg-surface p-3"
              data-testid="recipe-import-failed"
            >
              <div className="flex items-center gap-2.5">
                <AlertCircle className="h-4 w-4 shrink-0 text-fg-3" aria-hidden="true" />
                <div>
                  <div className="text-[14px] font-semibold text-fg">That page fought back</div>
                  <div className="text-xs text-fg-3">
                    Some sites bury the recipe. Yours isn&apos;t lost — type it in below.
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode('manual')}
                  className="h-10 flex-1 rounded-md bg-inverse text-[14px] font-semibold text-inverse-fg"
                >
                  Type it in
                </button>
                <button
                  type="button"
                  onClick={() => void handleImport()}
                  className="h-10 rounded-md border-2 border-line-strong px-3.5 text-[14px] font-semibold text-fg"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {showCleanupOffer(importState) && (
            <div
              className="flex flex-col gap-2.5 rounded-md border border-line bg-surface p-3"
              data-testid="recipe-ai-cleanup"
            >
              {cleanup.phase === 'idle' && (
                <>
                  <button
                    type="button"
                    data-testid="recipe-ai-cleanup-button"
                    onClick={() => void handleCleanup()}
                    className="flex h-10 items-center justify-center gap-2 rounded-md bg-inverse px-4 text-[14px] font-semibold text-inverse-fg"
                  >
                    <Sparkles className="h-4 w-4" aria-hidden="true" />
                    Clean it up with the assistant
                  </button>
                  <div className="text-xs text-fg-3">
                    It reads the page and fills the form in below. You still check it and save.
                  </div>
                </>
              )}

              {cleanup.phase === 'working' && (
                <div className="flex items-center gap-2.5" data-testid="recipe-ai-cleanup-working">
                  <Loader2
                    className="h-4 w-4 shrink-0 animate-spin text-sync-busy"
                    aria-hidden="true"
                  />
                  <div>
                    <div className="text-[14px] font-semibold text-fg">Having a look…</div>
                    <div className="text-xs text-fg-3">Usually under a minute.</div>
                  </div>
                </div>
              )}

              {cleanup.phase === 'done' && (
                <div className="flex items-center gap-2.5" data-testid="recipe-ai-cleanup-done">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-sync-ok" aria-hidden="true" />
                  <div>
                    <div className="text-[14px] font-semibold text-fg">
                      Tidied — {cleanup.ingredientCount} ingredients, {cleanup.stepCount} steps
                    </div>
                    <div className="text-xs text-fg-3">
                      Nothing is saved yet. Look it over below.
                    </div>
                  </div>
                </div>
              )}

              {/* An LLM outage is never an error state. Quiet note, form still works. */}
              {(cleanup.phase === 'napping' || cleanup.phase === 'waiting') && (
                <div className="text-xs text-fg-3" data-testid="recipe-ai-cleanup-napping">
                  {cleanup.phase === 'waiting'
                    ? 'The assistant is still thinking — it’ll finish on its own. Type it in below in the meantime.'
                    : 'The assistant is napping. Type it in below — nothing is lost.'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {(mode === 'manual' || importState.phase === 'done') && (
        <div className="flex flex-col gap-3 px-4" data-testid="recipe-manual-fields">
          {draft.photoRef && (
            <div
              className="flex items-center gap-2.5 rounded-md border border-line bg-surface p-2"
              data-testid="recipe-form-photo-preview"
            >
              <img
                src={recipeThumbUrl(draft.photoRef.id)}
                alt=""
                className="h-14 w-14 shrink-0 rounded object-cover"
              />
              <span className="text-xs text-fg-3">
                Photo found on the page — it'll be saved with this recipe.
              </span>
            </div>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
              Title
            </span>
            <input
              data-testid="recipe-title-input"
              required
              value={draft.title}
              onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Crepes"
              className="h-11 rounded border-2 border-line-strong bg-surface px-3 text-[15px] font-medium text-fg focus:outline-none"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
              Servings (optional — powers the scaling stepper)
            </span>
            <input
              data-testid="recipe-servings-input"
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              value={draft.servings ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                setDraft((prev) => ({
                  ...prev,
                  servings: raw.trim() === '' ? undefined : Math.max(1, Math.round(Number(raw))),
                }));
              }}
              placeholder="4"
              className="h-11 w-24 rounded border-2 border-line-strong bg-surface px-3 text-[15px] font-medium text-fg focus:outline-none"
            />
          </label>

          <div className="flex flex-col gap-2 rounded-md border border-line bg-surface p-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
              Ingredients — one per line
            </span>
            <div className="flex flex-col gap-1.5" data-testid="recipe-ingredient-rows">
              {draft.ingredients.map((ing, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    data-testid="recipe-ingredient-input"
                    value={ing.text}
                    onChange={(e) => updateIngredient(i, e.target.value)}
                    placeholder="2 cups flour"
                    className="h-9 flex-1 rounded border border-line bg-bg px-2 text-[14px] text-fg focus:outline-none"
                  />
                  {ing.unit && <span className="shrink-0 text-xs text-fg-3">{ing.unit}</span>}
                  <button
                    type="button"
                    aria-label="Remove ingredient"
                    onClick={() => removeIngredientRow(i)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center text-fg-3"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid="recipe-add-ingredient-row"
              onClick={addIngredientRow}
              className="flex items-center gap-1 self-start text-[13px] font-semibold text-accent"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> add another
            </button>
          </div>

          <div className="flex flex-col gap-2 rounded-md border border-line bg-surface p-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
              Steps
            </span>
            <div className="flex flex-col gap-1.5" data-testid="recipe-step-rows">
              {draft.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="mt-2 shrink-0 font-mono text-xs font-semibold text-accent">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <textarea
                    data-testid="recipe-step-input"
                    value={step}
                    onChange={(e) => updateStep(i, e.target.value)}
                    rows={2}
                    placeholder="Roast and peel the chiles."
                    className="h-auto min-h-9 flex-1 rounded border border-line bg-bg px-2 py-1.5 text-[14px] text-fg focus:outline-none"
                  />
                  <button
                    type="button"
                    aria-label="Remove step"
                    onClick={() => removeStep(i)}
                    className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center text-fg-3"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid="recipe-add-step-row"
              onClick={addStep}
              className="flex items-center gap-1 self-start text-[13px] font-semibold text-accent"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> add a step
            </button>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
              Tags
            </span>
            <div className="flex flex-wrap items-center gap-1.5">
              {draft.tags.map((tag) => (
                <span
                  key={tag}
                  className="flex h-7 items-center gap-1 rounded-full bg-raised px-2.5 text-xs font-semibold text-fg"
                >
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              ))}
              <input
                data-testid="recipe-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Weeknight, add with enter"
                className="h-8 min-w-[8rem] flex-1 rounded border border-line bg-bg px-2 text-[13px] text-fg focus:outline-none"
              />
            </div>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
              Notes
            </span>
            <textarea
              data-testid="recipe-notes-input"
              value={draft.notes ?? ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="rounded border border-line bg-surface px-3 py-2 text-[14px] text-fg focus:outline-none"
            />
          </label>

          <p className="text-xs text-fg-3">
            Quantities get parsed as you type — wrong guesses are one tap to fix and never block
            saving.
          </p>
        </div>
      )}

      <div className="fixed inset-x-0 bottom-14 z-10 border-t border-line bg-surface px-4 py-2.5 lg:bottom-0">
        <button
          type="submit"
          data-testid="recipe-save-button"
          disabled={!draft.title.trim()}
          className="h-12 w-full rounded-md bg-inverse text-[15px] font-semibold text-inverse-fg disabled:opacity-50"
        >
          Save recipe
        </button>
      </div>
    </form>
  );
}
